<template>
  <v-card flat class="ach-input-form">
    <v-container class="pa-6">
      <v-row v-if="showACHHeader">
        <v-col>
          <div class="d-flex flex-column">
            <div class="d-flex justify-space-between">
              <div>
                <v-icon> mdi-bank </v-icon>
                {{ titleText }}
              </div>
            </div>
            <div class="subtitle">
              {{ subTitleText }}
            </div>
          </div>
        </v-col>
      </v-row>
      <v-fade-transition>
        <v-row v-if="cardError || addCardError">
          <v-col>
            <v-alert color="secondary">
              <template v-slot:prepend>
                <v-icon color="white" class="mr-2">mdi-alert</v-icon>
              </template>
              {{ computedCardErrorMessage }}
            </v-alert>
          </v-col>
        </v-row>
      </v-fade-transition>
      <v-progress-circular
        :size="120"
        indeterminate
        v-if="localChargifyLoading"
        class="chargify-progress"
      />
      <v-row
        id="ach-chargify-form"
        :class="{ 'hide-chargify-form': localChargifyLoading }"
      >
        <v-col cols="9" class="pr-6 pb-0">
          <div class="d-flex flex-column">
            <div class="d-flex">
              <div class="mr-2">
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.achFormFields.firstName') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="ach_firstName" />
              </div>
              <div>
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.achFormFields.lastName') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="ach_lastName" />
              </div>
            </div>
            <div class="mr-2">
              <div class="input-label">
                <span class="caption font-weight-light">
                  {{ $t('dealer.chargify.achFormFields.bankName') }}
                </span>
                <span class="required-field-asterisk">*</span>
              </div>
              <div id="ach_bankName" />
            </div>
          </div>
          <div class="d-flex">
            <div class="mr-2">
              <div class="input-label">
                <span class="caption font-weight-light">
                  {{ $t('dealer.chargify.achFormFields.routingNumber') }}
                </span>
                <span class="required-field-asterisk">*</span>
              </div>
              <div id="ach_routingNumber" />
            </div>
            <div>
              <div>
                <div class="input-label">
                  <span class="caption font-weight-light">
                    {{ $t('dealer.chargify.achFormFields.accountNumber') }}
                  </span>
                  <span class="required-field-asterisk">*</span>
                </div>
                <div id="ach_accountNumber" />
              </div>
            </div>
          </div>
          <div>
            <div class="input-label">
              <span class="caption font-weight-light">
                {{
                  $t('dealer.chargify.achFormFields.accountHolderType')
                }}</span
              >
              <span class="required-field-asterisk">*</span>
            </div>
            <div id="ach_accountHolderType" />
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pt-0">
          <div class="text-body-2">
            <v-checkbox
              dense
              hide-details
              small
              @change="checkHerebyAuthorized"
              v-model="herebyAuthorized"
            >
              <template v-slot:label>
                <div class="text-body-2">
                  <i18n
                    path="dealer.chargify.achCheckbox"
                    tag="div"
                    for="dealer.chargify.salesMail"
                  >
                    <i class="mail-color">{{
                      $t('dealer.chargify.salesMail')
                    }}</i>
                  </i18n>
                </div>
              </template>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>
      <v-fade-transition>
        <v-row v-if="!localChargifyLoading && showACHButtons">
          <v-col offset="6">
            <div class="d-flex justify-end">
              <v-btn
                @click="decline"
                class="mr-2"
                :loading="localLogoutLoading"
              >
                <template v-if="showCloseButton">
                  {{ $t('global.cancel') }}
                </template>
                <template v-else>
                  {{ $t('dealer.chargify.declineAndLogout') }}
                </template>
              </v-btn>
              <v-btn
                @click="addPaymentClick"
                color="secondary"
                v-if="dealerAdminOnly"
                :loading="paymentLoading"
              >
                {{ $t('dealer.chargify.addPaymentMethod') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-fade-transition>
    </v-container>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import chargify from '@/mixins/chargify'

export default defineComponent({
  name: 'ACHEntryForm',
  mixins: [chargify],
  props: {
    titleText: {
      type: String,
      default: '',
    },
    subTitleText: {
      type: String,
      default: '',
    },
    showACHButtons: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cardError: false,
      cardErrorMessage: '',
      localChargifyLoading: false,
      paymentLoading: false,
      herebyAuthorized: false,
    }
  },
  computed: {
    ...mapState('Payment', ['addCardError', 'addCardErrorMessage']),
    ccTitleText() {
      return this.$t('dealer.chargify.addPayment')
    },
    computedCardErrorMessage() {
      if (this.cardErrorMessage == '') {
        return this.addCardErrorMessage
      } else {
        if (Array.isArray(this.cardErrorMessage)) {
          let messageItem = this.cardErrorMessage[0]
          return messageItem.replace(':', '')
        } else {
          return this.cardErrorMessage
        }
      }
    },
    showACHHeader() {
      if (this.titleText == '' && this.subTitleText == '') {
        return false
      } else {
        return true
      }
    },
    dealerAdminOnly() {
      return true
    },
  },
  methods: {
    ...mapActions('Payment', [
      'addNewCard',
      'resetCardError',
      'callAddPaymentLoading',
      'callACHAuthorized',
    ]),
    decline() {
      if (this.showCloseButton) {
        this.closeDialog()
      } else {
        this.$emit('decline')
      }
    },
    ...mapActions('ToastMessage', ['callSuccessToast']),
    async addPaymentClick() {
      this.paymentLoading = true
      this.callAddPaymentLoading(this.paymentLoading)
      this.cardErrorMessage = ''
      this.cardError = false
      try {
        const token = await this.submitCardInfo('ach-chargify-form')
        let payload = {
          chargify_token: token,
        }
        await this.addNewCard(payload)
        if (!this.addCardError) {
          this.closeDialog()
          this.callSuccessToast({
            toastMessage: this.$t('dealer.chargify.paymentMethodAdded'),
          })
        }
        this.paymentLoading = false
        this.callAddPaymentLoading(this.paymentLoading)
      } catch (err) {
        if (err.errors) {
          this.cardError = true
          this.cardErrorMessage = err.errors
        }
        this.paymentLoading = false
        this.callAddPaymentLoading(this.paymentLoading)
      }
    },
    checkHerebyAuthorized(type) {
      this.callACHAuthorized(type)
    },
    closeDialog() {
      this.$emit('closeDialog')
      this.chargify.unload()
    },
    reloadCard() {
      this.cardErrorMessage = ''
      this.cardError = false
      this.resetCardError()
      this.chargify.unload()
      this.loadChargifyWithLoading()
      this.herebyAuthorized = false
      this.callACHAuthorized(this.herebyAuthorized)
    },
    loadChargifyWithLoading() {
      this.localChargifyLoading = true
      this.loadChargify('bank')
      setTimeout(() => {
        this.localChargifyLoading = false
      }, 2000)
    },
  },
  mounted() {
    this.loadChargifyWithLoading()
    this.resetCardError()
  },
})
</script>

<style lang="scss">
.ach-input-form {
  overflow: hidden;
  .chargify-progress.v-progress-circular {
    position: absolute;
    top: 150px;
    left: 200px;
  }
  .v-input {
    font-size: 14px;
  }
  .v-text-field.v-text-field--enclosed .v-text-field__details {
    margin-bottom: 0px !important;
  }
  .v-icon__svg {
    fill: black;
  }

  .v-text-field .v-input__control .v-input__slot {
    font-size: 14px;
    min-height: 20px !important;
    margin-bottom: 4px !important;
  }
  .v-text-field--box .v-input__slot,
  .v-text-field--outline .v-input__slot {
    min-height: 20px !important;
    margin-bottom: 4px !important;
  }
  #ach_firstName iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #ach_lastName iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #ach_bankName iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #ach_accountHolderType iframe {
    width: 50% !important;
    cursor: pointer;
    overflow: hidden;
    min-height: 61px;
    max-height: 65px;
  }
  #ach_routingNumber iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #ach_accountNumber iframe {
    width: 100% !important;
    max-height: 65px;
  }
  #ach-chargify-form {
    visibility: visible;
    opacity: 1;
    transition: visibility 0s, opacity 0.5s linear;
  }
  .hide-chargify-form {
    transition: visibility 0s, opacity 0.5s linear !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }
  .mail-color {
    color: #0091ff;
  }
}
</style>
